/**
 * bankImport/money — Float-freie Betragskonvertierung für Bank-Importe.
 *
 * NIE `parseFloat(x)*100` (golden rules Integer-Cents-Disziplin). Zerlegt den
 * String rein über Regex/String-Operationen in Integer-Cents.
 *
 * Kanonische Soll-Paare (architecture-bank-reconciliation.md §5.3) sind
 * bindend — bei Widerspruch zu einer älteren Doku-Zelle gilt §5.3 v2.
 *
 * @module bankImport/money
 */

/**
 * Wandelt einen dezimalen Betrags-String in Integer-Cents.
 *
 * `style:'en'` (ISO/CAMT): Punkt als Dezimaltrenner, KEIN Tausendertrenner,
 * bis zu 5 Nachkommastellen (ISO 20022, BR-16 #5). EUR-Cent-Semantik bleibt
 * 2-stellig: Stellen 3–5 mit nachlaufenden Nullen werden gekürzt, ein
 * signifikanter Sub-Cent-Rest (≠0) → Fehler (wird NIE gerundet).
 *
 * `style:'de'`: Komma als Dezimaltrenner (1–5 Nachkommastellen, gleiche
 * Sub-Cent-Semantik wie 'en'), Punkt als
 * optionaler Tausendertrenner in Gruppen von exakt 3 Ziffern. Ohne Komma wird
 * ein Punkt-gruppierter String als Tausendertrennung interpretiert
 * (`"12.345"` → `1234500`, § 5.3).
 *
 * @param raw - Roh-Betrag, z.B. `"1.234,56"` oder `"-15,00"`.
 * @param style - `'de'` oder `'en'` Zahlenformat.
 * @returns Integer-Cents, signiert.
 * @throws {Error} Bei leerem, mehrdeutigem oder unparsebarem Input (fail-closed —
 *   die Zeile wird vom Aufrufer nie stillschweigend übernommen).
 *
 * @example
 * centsFromDecimalString('1234.56', 'en')   // 123456
 * centsFromDecimalString('-15,00', 'de')    // -1500
 * centsFromDecimalString('1.234,56', 'de')  // 123456
 * centsFromDecimalString('12.345', 'de')    // 1234500 (Tausendertrenner)
 * centsFromDecimalString('12.345', 'en')    // throws (>2 Nachkommastellen)
 */
export function centsFromDecimalString(raw: string, style: 'de' | 'en'): number {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) {
    throw new Error('centsFromDecimalString: leerer Betrag.');
  }
  return style === 'en' ? parseEnStyle(trimmed) : parseDeStyle(trimmed);
}

function parseEnStyle(trimmed: string): number {
  // BR-16 #5: ISO 20022 `ActiveOrHistoricCurrencyAndAmount` erlaubt bis 5
  // fractionDigits. Regex auf 1–5 Nachkommastellen geweitet; die Sub-Cent-
  // Semantik entscheidet `centsFromFraction` (kürzen bei Null-Padding,
  // fail-closed bei signifikantem Rest).
  const match = /^([+-]?)(\d+)(?:\.(\d{1,5}))?$/.exec(trimmed);
  if (!match) {
    throw new Error(`centsFromDecimalString: unparsebarer Betrag (style 'en'): "${trimmed}".`);
  }
  const [, signStr, intPart, fracPart] = match;
  const sign = signStr === '-' ? -1 : 1;
  return sign * (Number(intPart) * 100 + centsFromFraction(fracPart ?? '', trimmed));
}

/**
 * Nachkomma-Ziffern → Cent (BR-16 #5). EUR-Cent-Semantik bleibt 2-stellig;
 * ISO erlaubt jedoch bis 5 fractionDigits (z.B. gepaddete `300` oder
 * Fremdwährungs-Notation). Behandlung der Stellen 3–5:
 *  - ausschließlich nachlaufende Nullen (`"300"`, `"5000"`) → auf Cent gekürzt
 *    (verlustfrei, kein Geld verändert).
 *  - signifikanter Rest ≠ 0 (`"305"`) → fail-closed `throw` (NIE runden, sonst
 *    würde Geld verfälscht — die Zeile landet beim Aufrufer in `errors[]`).
 * Für 0–2 Stellen ist das Ergebnis bit-identisch zum bisherigen `padEnd(2,'0')`.
 */
function centsFromFraction(fracPart: string, original: string): number {
  const centDigits = fracPart.slice(0, 2).padEnd(2, '0');
  const subCent = fracPart.slice(2);
  if (subCent.length > 0 && /[1-9]/.test(subCent)) {
    throw new Error(
      `centsFromDecimalString: Betrag "${original}" hat signifikante Sub-Cent-Stellen — ` +
        'wird nicht gerundet (fail-closed).'
    );
  }
  return Number(centDigits);
}

function parseDeStyle(trimmed: string): number {
  const signMatch = /^([+-]?)(.+)$/.exec(trimmed);
  if (!signMatch) {
    throw new Error(`centsFromDecimalString: unparsebarer Betrag (style 'de'): "${trimmed}".`);
  }
  const sign = signMatch[1] === '-' ? -1 : 1;
  const body = signMatch[2];

  if (body.includes(',')) {
    // Dezimaltrenner Komma; optionale Tausendergruppen (Punkt) davor.
    // BR-16 #5: Nachkommastellen auf 1–5 geweitet (Symmetrie zum en-Pfad);
    // Sub-Cent-Semantik via `centsFromFraction` (kürzen/fail-closed). Für 1–2
    // Stellen unverändert.
    const commaMatch = /^(\d{1,3}(?:\.\d{3})*|\d+),(\d{1,5})$/.exec(body);
    if (!commaMatch) {
      throw new Error(`centsFromDecimalString: unparsebarer Betrag (style 'de'): "${trimmed}".`);
    }
    const intDigits = commaMatch[1].replace(/\./g, '');
    return sign * (Number(intDigits) * 100 + centsFromFraction(commaMatch[2], trimmed));
  }

  // Kein Komma: entweder reine Ziffernfolge oder Tausender-gruppiert mit
  // Punkt in Gruppen von exakt 3 Ziffern (§5.3: "12.345" = 12.345 EUR).
  if (/^\d{1,3}(?:\.\d{3})+$/.test(body)) {
    return sign * Number(body.replace(/\./g, '')) * 100;
  }
  if (/^\d+$/.test(body)) {
    return sign * Number(body) * 100;
  }
  throw new Error(`centsFromDecimalString: unparsebarer Betrag (style 'de'): "${trimmed}".`);
}
